import { useState, useEffect, FormEvent } from 'react';
import { supabase } from '../../../lib/supabase';
import BookingCalendar from './BookingCalendar';
import BookingSlots from './BookingSlots';

const MONTHS = ['Enero','Febrero','Marzo','Abril','Mayo','Junio','Julio','Agosto','Septiembre','Octubre','Noviembre','Diciembre'];

function formatDate(dateStr: string): string {
  const d = new Date(dateStr + 'T12:00:00');
  return `${d.getDate()} de ${MONTHS[d.getMonth()]} de ${d.getFullYear()}`;
}

const EMPTY_FORM = { nombre: '', telefono: '', email: '', modelo_coche: '', matricula: '', servicio: '', notas: '' };

const AppointmentForm = () => {
  const [step, setStep] = useState<1 | 2 | 3 | 4>(1);
  const [selectedDate, setSelectedDate] = useState<string | null>(null);
  const [selectedTime, setSelectedTime] = useState<string | null>(null);
  const [occupiedMap, setOccupiedMap] = useState<Record<string, string[]>>({});
  const [loadingSlots, setLoadingSlots] = useState(true);
  const [formData, setFormData] = useState(EMPTY_FORM);
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    const fetchOccupied = async () => {
      setLoadingSlots(true);
      const today = new Date().toISOString().split('T')[0];
      const { data } = await supabase
        .from('TALLERMECANICOKEVIN_appointments')
        .select('fecha, hora')
        .gte('fecha', today)
        .not('estado', 'eq', 'cancelada');
      const map: Record<string, string[]> = {};
      for (const row of data ?? []) {
        if (!map[row.fecha]) map[row.fecha] = [];
        map[row.fecha].push(row.hora?.slice(0, 5) ?? row.hora);
      }
      setOccupiedMap(map);
      setLoadingSlots(false);
    };
    fetchOccupied();
  }, []);

  const handleDateSelect = (date: string) => {
    setSelectedDate(date);
    setSelectedTime(null);
    setStep(2);
  };

  const handleTimeSelect = (time: string) => {
    setSelectedTime(time);
    setStep(3);
  };

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();
    if (!selectedDate || !selectedTime) return;
    setSubmitting(true);
    try {
      const { error } = await supabase
        .from('TALLERMECANICOKEVIN_appointments')
        .insert({
          ...formData,
          servicio: formData.servicio || null,
          fecha: selectedDate,
          hora: selectedTime,
          estado: 'pendiente',
          origen: 'web',
        });
      if (!error) {
        setOccupiedMap((prev) => {
          const updated = { ...prev };
          if (!updated[selectedDate]) updated[selectedDate] = [];
          updated[selectedDate] = [...updated[selectedDate], selectedTime];
          return updated;
        });
        setStep(4);
      }
    } finally {
      setSubmitting(false);
    }
  };

  const reset = () => {
    setStep(1);
    setSelectedDate(null);
    setSelectedTime(null);
    setFormData(EMPTY_FORM);
  };

  if (loadingSlots) {
    return (
      <div className="flex flex-col items-center justify-center py-12 gap-3">
        <i className="ri-loader-4-line animate-spin text-yellow-400 text-2xl" />
        <p className="text-zinc-500 text-sm">Cargando disponibilidad...</p>
      </div>
    );
  }

  if (step === 4) {
    return (
      <div className="flex flex-col items-center justify-center py-12 gap-4">
        <div className="w-16 h-16 flex items-center justify-center bg-yellow-400/10 border border-yellow-400/30 text-yellow-400 text-3xl">
          <i className="ri-calendar-check-line" />
        </div>
        <p className="text-white font-black text-lg uppercase tracking-wide">¡Cita reservada!</p>
        <div className="bg-zinc-800 border border-zinc-700 px-5 py-3 text-center">
          <p className="text-yellow-400 font-black text-base">{selectedDate && formatDate(selectedDate)}</p>
          <p className="text-white text-sm font-bold">{selectedTime}h — {formData.servicio || 'Revisión general'}</p>
        </div>
        <p className="text-zinc-500 text-sm text-center max-w-xs">Te contactaremos para confirmar tu cita. Recuerda llegar 5 minutos antes.</p>
        <button onClick={reset} className="text-xs text-yellow-400 hover:text-white transition-colors cursor-pointer uppercase tracking-widest mt-2">
          Reservar otra cita
        </button>
      </div>
    );
  }

  return (
    <>
      <h2 className="text-white font-black text-lg uppercase mb-1">Reservar cita</h2>
      <p className="text-zinc-500 text-sm mb-5">Elige fecha y hora y confirma tus datos. Te contactamos para verificar.</p>

      {/* Progress */}
      <div className="flex items-center gap-2 mb-6">
        {[
          { n: 1, label: 'Fecha' },
          { n: 2, label: 'Hora' },
          { n: 3, label: 'Datos' },
        ].map(({ n, label }, i) => (
          <div key={n} className="flex items-center gap-2">
            {i > 0 && <div className={`flex-1 h-px w-8 ${step > n - 1 ? 'bg-yellow-400' : 'bg-zinc-800'}`} />}
            <div className={`w-6 h-6 flex items-center justify-center text-[10px] font-black border transition-all ${step >= n ? 'bg-yellow-400 border-yellow-400 text-black' : 'bg-zinc-900 border-zinc-700 text-zinc-600'}`}>
              {step > n ? <i className="ri-check-line" /> : n}
            </div>
            <span className={`text-[10px] font-bold uppercase tracking-wider hidden sm:block ${step >= n ? 'text-yellow-400' : 'text-zinc-600'}`}>{label}</span>
          </div>
        ))}
      </div>

      {/* Step 1: Calendar */}
      {step === 1 && (
        <BookingCalendar
          occupiedMap={occupiedMap}
          selectedDate={selectedDate}
          onSelect={handleDateSelect}
        />
      )}

      {/* Step 2: Time slots */}
      {step === 2 && selectedDate && (
        <BookingSlots
          date={selectedDate}
          occupiedMap={occupiedMap}
          selectedTime={selectedTime}
          onSelect={handleTimeSelect}
          onBack={() => setStep(1)}
        />
      )}

      {/* Step 3: Form */}
      {step === 3 && selectedDate && selectedTime && (
        <form onSubmit={handleSubmit} className="space-y-4">
          <button type="button" onClick={() => setStep(2)} className="flex items-center gap-2 text-zinc-500 hover:text-yellow-400 text-xs font-bold uppercase tracking-wider cursor-pointer transition-colors mb-2 whitespace-nowrap">
            <i className="ri-arrow-left-line" /> Cambiar hora
          </button>

          <div className="bg-zinc-800 border border-zinc-700 px-4 py-3 flex items-center gap-3 mb-4">
            <div className="w-8 h-8 flex items-center justify-center text-yellow-400"><i className="ri-calendar-check-line text-lg" /></div>
            <div>
              <p className="text-white font-bold text-sm">{formatDate(selectedDate)}</p>
              <p className="text-zinc-400 text-xs">{selectedTime}h</p>
            </div>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <label className="block text-xs font-bold uppercase tracking-wider text-zinc-400 mb-1.5">Nombre *</label>
              <input name="nombre" required value={formData.nombre} onChange={(e) => setFormData((p) => ({ ...p, nombre: e.target.value }))} placeholder="Tu nombre" className="w-full bg-zinc-800 border border-zinc-700 text-white text-sm px-4 py-2.5 outline-none focus:border-yellow-400/50 transition-colors placeholder:text-zinc-600" />
            </div>
            <div>
              <label className="block text-xs font-bold uppercase tracking-wider text-zinc-400 mb-1.5">Teléfono *</label>
              <input name="telefono" required type="tel" value={formData.telefono} onChange={(e) => setFormData((p) => ({ ...p, telefono: e.target.value }))} placeholder="6XX XXX XXX" className="w-full bg-zinc-800 border border-zinc-700 text-white text-sm px-4 py-2.5 outline-none focus:border-yellow-400/50 transition-colors placeholder:text-zinc-600" />
            </div>
          </div>
          <div>
            <label className="block text-xs font-bold uppercase tracking-wider text-zinc-400 mb-1.5">Email</label>
            <input name="email" type="email" value={formData.email} onChange={(e) => setFormData((p) => ({ ...p, email: e.target.value }))} placeholder="tu@email.com" className="w-full bg-zinc-800 border border-zinc-700 text-white text-sm px-4 py-2.5 outline-none focus:border-yellow-400/50 transition-colors placeholder:text-zinc-600" />
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <label className="block text-xs font-bold uppercase tracking-wider text-zinc-400 mb-1.5">Vehículo</label>
              <input name="modelo_coche" value={formData.modelo_coche} onChange={(e) => setFormData((p) => ({ ...p, modelo_coche: e.target.value }))} placeholder="Marca y modelo" className="w-full bg-zinc-800 border border-zinc-700 text-white text-sm px-4 py-2.5 outline-none focus:border-yellow-400/50 transition-colors placeholder:text-zinc-600" />
            </div>
            <div>
              <label className="block text-xs font-bold uppercase tracking-wider text-zinc-400 mb-1.5">Matrícula</label>
              <input name="matricula" value={formData.matricula} onChange={(e) => setFormData((p) => ({ ...p, matricula: e.target.value }))} placeholder="0000 XXX" className="w-full bg-zinc-800 border border-zinc-700 text-white text-sm px-4 py-2.5 outline-none focus:border-yellow-400/50 transition-colors placeholder:text-zinc-600 uppercase" />
            </div>
          </div>
          <div>
            <label className="block text-xs font-bold uppercase tracking-wider text-zinc-400 mb-1.5">Servicio</label>
            <select name="servicio" value={formData.servicio} onChange={(e) => setFormData((p) => ({ ...p, servicio: e.target.value }))} className="w-full bg-zinc-800 border border-zinc-700 text-zinc-400 text-sm px-4 py-2.5 outline-none focus:border-yellow-400/50 transition-colors cursor-pointer">
              <option value="">Selecciona un servicio</option>
              <option>Revisión periódica</option>
              <option>Diagnóstico electrónico</option>
              <option>Frenos y suspensión</option>
              <option>Chapa y pintura</option>
              <option>Climatización</option>
              <option>Compraventa de vehículo</option>
              <option>Otro</option>
            </select>
          </div>
          <div>
            <label className="block text-xs font-bold uppercase tracking-wider text-zinc-400 mb-1.5">Notas adicionales</label>
            <textarea name="notas" maxLength={500} rows={3} value={formData.notas} onChange={(e) => setFormData((p) => ({ ...p, notas: e.target.value }))} placeholder="Describe brevemente la avería o el servicio que necesitas..." className="w-full bg-zinc-800 border border-zinc-700 text-white text-sm px-4 py-2.5 outline-none focus:border-yellow-400/50 transition-colors resize-none placeholder:text-zinc-600" />
          </div>
          <button type="submit" disabled={submitting}
            className="w-full flex items-center justify-center gap-2 bg-yellow-400 text-black font-black text-xs uppercase tracking-widest py-3.5 hover:bg-yellow-300 transition-all cursor-pointer disabled:opacity-60 whitespace-nowrap">
            {submitting ? <><i className="ri-loader-4-line animate-spin" /> Reservando...</> : <><i className="ri-calendar-check-line" /> Confirmar reserva</>}
          </button>
        </form>
      )}
    </>
  );
};

export default AppointmentForm;
